import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ApiRequest } from 'GlobalFunctions';
import AutocompleteField_su from "elements/AutocompleteField_su";
import { Dropdown, Form } from 'semantic-ui-react';

export default function TaxesRatesEditor(props: any) {
    /*============================================= 
    VARIABLES FROM PROPS
    ============================================= */
    const {
        setLoading,
        openEditor,
        setOpenEditor,
        id,
        loadList
    } = props;

    /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
    const { enqueueSnackbar } = useSnackbar();
    /*============================================= */

    const [formValues, setFormValues] = useState<any>({
        id: '',
        tax_type_id: '',
        country_id: '',
        region_id: '',
        city_id: '',
        rate: '',
        category_id: '',
        is_exclusive: false
    });

    const [taxTypes, setTaxTypes] = useState<any>([]);
    const [countries, setCountries] = useState<any>([]);
    const [regions, setRegions] = useState<any>([]);
    const [cities, setCities] = useState<any>([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    /*=======================================
    GET DETAILS
    =======================================*/
    useEffect(() => {
        if (id && openEditor) {
            getDetails();
        } else if (openEditor && !id) {
            // Reset form for new tax rate
            setFormValues({
                id: '',
                tax_type_id: '',
                country_id: '',
                region_id: '',
                city_id: '',
                rate: '',
                category_id: '',
                is_exclusive: false
            });
        }
    }, [openEditor, id]);

    /*=======================================
    LOAD DROPDOWNS DATA
    =======================================*/
    useEffect(() => {
        if (openEditor) {
            loadTaxTypes();
            loadCountries();
        }
    }, [openEditor]);

    const getDetails = async () => {
        setLoading(true);
        
        await ApiRequest({
            method: 'GET',
            url: `/admin/taxes/rates/details/${id}`,
            setResponse: (response: any) => {
                setFormValues(response || {});
            }
        });

        setLoading(false);
    }

    const loadTaxTypes = async () => {
        await ApiRequest({
            method: 'GET',
            url: '/admin/taxes/types/list',
            query: { full_list: true },
            setResponse: (response: any) => {
                if (response.list) {
                    setTaxTypes(response.list);
                }
            }
        });
    }



    const loadCountries = async () => {
        await ApiRequest({
            method: 'GET',
            url: '/countries/list',
            query: { full_list: true },
            setResponse: (response: any) => {
                if (response.list) {
                    setCountries(response.list);
                }
            }
        });
    }

    const loadRegions = async (countryId: string) => {
        if (!countryId) {
            setRegions([]);
            return;
        }

        await ApiRequest({
            method: 'GET',
            url: '/countries/getRegions',
            query: { country_id: countryId },
            setResponse: (response: any) => {
                if (response.list) {
                    setRegions(response.list);
                } else {
                    setRegions([]);
                }
            }
        });
    }

    const loadCities = async (regionId: string) => {
        if (!regionId) {
            setCities([]);
            return;
        }

        await ApiRequest({
            method: 'GET',
            url: '/countries/getCities',
            query: { region_id: regionId },
            setResponse: (response: any) => {
                if (response.list) {
                    setCities(response.list);
                } else {
                    setCities([]);
                }
            }
        });
    }

    /*=======================================
    HANDLE FIELD CHANGES
    =======================================*/
    const handleChange = (field: string, value: any) => {
        setFormValues({ ...formValues, [field]: value });

        // Load regions when country changes
        if (field === 'country_id') {
            setFormValues({ ...formValues, [field]: value, region_id: '', city_id: '' });
            setRegions([]);
            setCities([]);
            if (value) {
                loadRegions(value);
            }
        }

        // Load cities when region changes
        if (field === 'region_id') {
            setFormValues({ ...formValues, [field]: value, city_id: '' });
            setCities([]);
            if (value) {
                loadCities(value);
            }
        }
    }

    /*=======================================
    HANDLE COUNTRY CHANGE
    =======================================*/
    const handleCountryChange = (data: any) => {
        setFormValues({
            ...formValues,
            country_id: data ? data.value : '',
            region_id: '',
            city_id: ''
        });
        setRegions([]);
        setCities([]);
        if (data && data.value) {
            loadRegions(data.value);
        }
    }

    /*=======================================
    HANDLE REGION CHANGE  
    =======================================*/
    const handleRegionChange = (data: any) => {
        setFormValues({
            ...formValues,
            region_id: data ? data.value : '',
            city_id: ''
        });
        setCities([]);
        if (data && data.value) {
            loadCities(data.value);
        }
    }

    /*=======================================
    HANDLE CITY CHANGE
    =======================================*/
    const handleCityChange = (value: any) => {
        setFormValues({
            ...formValues,
            city_id: value ? value.value : ''
        });
    }

    /*=======================================
    HANDLE CATEGORY CHANGE
    =======================================*/
    const handleCategoryChange = (value: any) => {
        setFormValues({
            ...formValues,
            category_id: value ? value.value : ''
        });
    }

    /*=======================================
    SUBMIT FUNCTION
    =======================================*/
    const handleSubmit = async () => {
        // Validation
        if (!formValues.tax_type_id) {
            enqueueSnackbar('Please select a tax type', { variant: 'error' });
            return;
        }

        if (!formValues.rate) {
            enqueueSnackbar('Please enter a tax rate', { variant: 'error' });
            return;
        }


        await ApiRequest({
            method: 'POST',
            url: '/admin/taxes/rates/addedit',
            body: formValues,
            setResponse: (response: any) => {
                if (response.status === "success") {
                    enqueueSnackbar(response.message || 'Tax rate saved successfully', { variant: 'success' });
                    setOpenEditor(false);
                    loadList();
                } else {
                    enqueueSnackbar(response.message || 'Error saving tax rate', { variant: 'error' });
                }
            }
        });
    };

    /*=======================================
    HANDLE CLOSE
    =======================================*/
    const handleClose = () => {
        setOpenEditor(false);
    }

    return (
        <Dialog 
            open={openEditor}
            onClose={handleClose}
            aria-labelledby="tax-rate-editor-title"
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { 
                    borderRadius: '16px',
                    // Modify overflow behavior when dropdown is open
                    overflow: dropdownOpen ? 'visible' : 'auto',
                    // Ensure the dialog still has a defined height
                    maxHeight: 'calc(100% - 64px)'
                }
            }}
        >
            <div className="DialogContainer">
                <div className="ModuleWrapper">
                    <div className="boxContainerHeader">
                        <div className="dialog_container_title">
                            <h2 className="dialog_title">{id ? 'Edit Tax Rate' : 'Add Tax Rate'}</h2>
                        </div>
                    </div>
                
                    <div className="dialogFormContainer">

                        <div className="form">
                            <div className="input_row">
                                <div className="textfield_wrapper">
                                    <InputLabel className="external_label">Tax Type</InputLabel>
                                    <TextField 
                                        fullWidth 
                                        required
                                        size="small"
                                        select
                                        value={formValues.tax_type_id || ''}
                                        onChange={(e) => handleChange('tax_type_id', e.target.value)}
                                    >
                                            {taxTypes.map((type: any) => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {type.name}
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                </div>

                                
                                <div className="textfield_wrapper">
                                    <InputLabel className="external_label">Category</InputLabel>
                                    <AutocompleteField_su
                                        endpoint="/admin/taxes/categories/list"
                                        value={formValues.category_id}
                                        className="autocomplete_small"
                                        onChange={handleCategoryChange}
                                        fullWidth
                                        allowClear={true}
                                        onOpen={() => setDropdownOpen(true)}
                                        onClose={() => setDropdownOpen(false)}
                                    />
                                </div>

                                <div className="textfield_wrapper">
                                    <InputLabel className="external_label">Tax Rate (%)</InputLabel>
                                    <TextField
                                        value={formValues.rate || ''}
                                        onChange={(e) => handleChange('rate', e.target.value)}
                                        fullWidth
                                        required
                                        size='small'
                                        type="number"
                                        inputProps={{ min: 0, max: 100, step: 0.001 }}
                                    />
                                </div>

                                
                            </div>
                            
                            <div className="input_row">
                                <div className="textfield_wrapper">
                                    <InputLabel className="external_label">Country</InputLabel>
                                    <AutocompleteField_su 
                                        endpoint="/countries/getCountriesV2"
                                        value={formValues.country_id}
                                        className="autocomplete_small"
                                        onChange={handleCountryChange}
                                        fullWidth
                                        onOpen={() => setDropdownOpen(true)}
                                        onClose={() => setDropdownOpen(false)}
                                    />
                                </div>

                                <div className="textfield_wrapper">
                                    <InputLabel className="external_label">State/Region</InputLabel>
                                    <AutocompleteField_su 
                                        endpoint="/countries/getRegions"
                                        endpoint_params={{ country_id: formValues.country_id }}
                                        value={formValues.region_id}
                                        className="autocomplete_small"
                                        onChange={handleRegionChange}
                                        fullWidth
                                        disabled={!formValues.country_id || formValues.country_id === '0'}
                                        onOpen={() => setDropdownOpen(true)}
                                        onClose={() => setDropdownOpen(false)}
                                    />
                                </div>

                                    <div className="textfield_wrapper">
                                        <InputLabel className="external_label">City</InputLabel>
                                        <AutocompleteField_su
                                            endpoint="/shipping/zones/getCities"
                                            endpoint_params={{ region_id: formValues.region_id }}
                                            max_results={10}
                                            dynamic={true}
                                            disabled={!formValues.region_id || formValues.region_id === '0'}
                                            value={formValues.city_id}
                                            className="autocomplete_small"
                                            onChange={handleCityChange}
                                            clearOnSelect={true}
                                            fullWidth
                                            onOpen={() => setDropdownOpen(true)}
                                            onClose={() => setDropdownOpen(false)}
                                        />
                                    </div>
                            </div>

                            <div className="textfield_wrapper">
                                <InputLabel className="external_label">Exclusive Tax</InputLabel>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formValues.is_exclusive || false}
                                            onChange={(e) => handleChange('is_exclusive', e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={formValues.is_exclusive ? "Yes - This tax overrides others" : "No - Can be combined with other taxes"}
                                />
                            </div>

                        </div>
                        
                    </div>
                </div>
            </div>
            
            <DialogActions>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                >
                    {id ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
